import { useState, useEffect, useMemo, useRef } from "react";
import { Groups, Endpoints, Gateways, GreasePump } from "../../../../Models/Assets/AssetTemplateReadDto";
import templatesApiService from "../../Utilities/TemplatesApiService";
import { getInitialValuesFromTemplate, stripTemplateNameSuffix } from "./assetTemplateDialogUtils";

export type TemplateSourceChoice = "new" | "existing";

/** Default positions: group and gateway side by side to avoid overlap (pins ~30px wide) */
const DEFAULT_GROUP_X = 50;
const DEFAULT_GROUP_Y = 50;
const DEFAULT_GATEWAY_X = 120;
const DEFAULT_GATEWAY_Y = 50;

const defaultGateway: Gateways = {
  id: "",
  gatewayId: "1",
  name: "Gateway 1",
  xPos: DEFAULT_GATEWAY_X,
  yPos: DEFAULT_GATEWAY_Y,
  status: "Active",
};

function createDefaultGroupAndEndpoint(): { group: Groups; endpoint: Endpoints } {
  const groupId = "1";
  const group: Groups = {
    id: groupId,
    name: "Group 1",
    parentId: "",
    imageId: "",
    xPos: DEFAULT_GROUP_X,
    yPos: DEFAULT_GROUP_Y,
    isEnabled: true,
    status: "Active",
    attachmentImageIds: [],
  };
  const endpoint: Endpoints = {
    id: `${groupId}-1`,
    groupId,
    greasePumpId: "",
    name: "Endpoint 1.1",
    pointId: 1,
    endpointDeviceId: "",
    endpointDeviceSerialNumber: "",
    requirementId: "",
    requirementName: "",
    xPos: 0,
    yPos: 0,
    isEnabled: true,
    status: "Active",
  };
  return { group, endpoint };
}

function mapGroups(raw: Groups[]): Groups[] {
  return raw.map((g: Groups, index: number) => {
    const hasValidId = g.id != null && String(g.id).trim() !== "";
    const id = hasValidId ? g.id : String(index + 1);
    return {
      ...g,
      id,
      status: g.status ?? (g.isEnabled !== false ? "Active" : "Inactive"),
    };
  });
}

function mapEndpoints(raw: Endpoints[], mappedGroups: Groups[] = []): Endpoints[] {
  const byGroup = new Map<string, number>();
  return raw.map((ep: Endpoints, index: number) => {
    const hasValidGroupId = ep.groupId != null && String(ep.groupId).trim() !== "";
    const groupId = hasValidGroupId
      ? ep.groupId
      : mappedGroups.length > 0
        ? mappedGroups[0].id
        : ep.groupId ?? "";
    const next = (byGroup.get(groupId) ?? 0) + 1;
    byGroup.set(groupId, next);
    const pointId = ep.pointId ?? next;
    const hasValidId = ep.id != null && String(ep.id).trim() !== "";
    const id = hasValidId ? ep.id : `${groupId}-${pointId}`;
    return {
      ...ep,
      id,
      groupId,
      pointId,
      status: ep.status ?? (ep.isEnabled !== false ? "Active" : "Inactive"),
    };
  });
}

function createDefaultGreasePump(): GreasePump {
  return {
    id: "1",
    name: "Grease Pump 1",
    deviceId: "",
    serialNo: "",
    channelNo: 0,
    xPos: 50,
    yPos: 50,
    status: "Active",
    devices: [],
  };
}

function mapGreasePumps(raw: GreasePump[]): GreasePump[] {
  return raw.map((p: GreasePump) => ({
    ...p,
    status: p.status ?? (p.isEnabled !== false ? "Active" : "Inactive"),
  }));
}

function mapGateways(raw: Gateways[]): Gateways[] {
  const mapped = raw.map((g: Gateways) => ({
    ...g,
    status: g.status ?? "Active",
  }));
  return mapped.length > 0 ? mapped : [defaultGateway];
}

export interface UseAssetTemplateDialogStateParams {
  assetTemplate: any;
  showTemplateSourceChoice: boolean;
  isAdministrationMode?: boolean;
  existingTemplates?: any[];
  currentSiteId: string;
  currentSiteName: string;
  isLoadingTemplate: boolean;
}

export function useAssetTemplateDialogState(params: UseAssetTemplateDialogStateParams) {
  const {
    assetTemplate,
    showTemplateSourceChoice,
    isAdministrationMode = false,
    currentSiteId,
    currentSiteName,
    isLoadingTemplate,
  } = params;

  const [sourceOption, setSourceOption] = useState<TemplateSourceChoice>("new");
  const [selectedExistingTemplateId, setSelectedExistingTemplateId] = useState<string | null>(null);
  const [baseTemplate, setBaseTemplate] = useState<any>(null);
  const [loadingCopyTemplate, setLoadingCopyTemplate] = useState(false);

  const effectiveTemplate = assetTemplate || baseTemplate;
  const showSourceChoiceInDetails = showTemplateSourceChoice && !assetTemplate;

  const handleExistingTemplateSelect = (templateId: string | null) => {
    setSelectedExistingTemplateId(templateId);
    if (!templateId) {
      setBaseTemplate(null);
      return;
    }
    setLoadingCopyTemplate(true);
    templatesApiService
      .getTemplateAsset(templateId)
      .then((data) => {
        setBaseTemplate({
          ...data,
          id: undefined,
          _sourceId: data.id,
          name: stripTemplateNameSuffix(data.name || ""),
        });
      })
      .catch((err) => {
        console.error("Error loading template for copy:", err);
      })
      .finally(() => setLoadingCopyTemplate(false));
  };

  const [step, setStep] = useState(0);
  const [hasGreasePumps, setHasGreasePumps] = useState(
    effectiveTemplate == null
      ? true
      : effectiveTemplate.isGreasePumpConnectivityEnabled || effectiveTemplate.hasGreasePumps || false
  );
  const _initialGroupsAndEndpoints = (() => {
    const mappedGroups = mapGroups(effectiveTemplate?.groups || []);
    const mappedEndpoints = mapEndpoints(effectiveTemplate?.endpoints || [], mappedGroups);
    if (mappedGroups.length > 0) {
      if (mappedEndpoints.length > 0) {
        return { groups: mappedGroups, endpoints: mappedEndpoints };
      }
      const firstGroup = mappedGroups[0];
      const { endpoint } = createDefaultGroupAndEndpoint();
      return {
        groups: mappedGroups,
        endpoints: [
          {
            ...endpoint,
            groupId: firstGroup.id,
            id: `${firstGroup.id}-1`,
          },
        ],
      };
    }
    const { group, endpoint } = createDefaultGroupAndEndpoint();
    return {
      groups: [group],
      endpoints: [{ ...endpoint, groupId: group.id, id: `${group.id}-1` }],
    };
  })();
  const [groups, setGroups] = useState<Groups[]>(_initialGroupsAndEndpoints.groups);
  const [endpoints, setEndpoints] = useState<Endpoints[]>(_initialGroupsAndEndpoints.endpoints);
  const [greasePumps, setGreasePumps] = useState<GreasePump[]>(() => {
    const mapped = mapGreasePumps(effectiveTemplate?.greasePumps || []);
    if (mapped.length > 0) return mapped;
    const hasGreasePumpsEnabled =
      effectiveTemplate == null ||
      effectiveTemplate?.isGreasePumpConnectivityEnabled ||
      effectiveTemplate?.hasGreasePumps;
    return hasGreasePumpsEnabled ? [createDefaultGreasePump()] : [];
  });
  const [gateways, setGateways] = useState<Gateways[]>(() =>
    mapGateways(effectiveTemplate?.gateways || [])
  );
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(() =>
    effectiveTemplate?.image ?? null
  );
  const [isLoadingImage, setIsLoadingImage] = useState(
    !!(effectiveTemplate?.imageId && !effectiveTemplate?.image)
  );

  const isEdit = !!assetTemplate;

  /** Track which template we've already synced from. Prevents re-syncing when user clicks groups
   * or interacts with the form, which could overwrite local state and cause endpoints to appear
   * in wrong groups (bug: endpoints from other groups being added to the selected group). */
  const lastSyncedTemplateKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (effectiveTemplate == null) {
      setHasGreasePumps(true);
    }
  }, [effectiveTemplate]);

  useEffect(() => {
    const imageId = effectiveTemplate?.imageId;
    if (!imageId || effectiveTemplate?.image) {
      setIsLoadingImage(false);
      return;
    }
    setIsLoadingImage(true);
    templatesApiService
      .getImageById(imageId)
      .then((res) => {
        setImageBase64(res?.data ?? null);
      })
      .catch((err) => {
        console.error("Error loading template image:", err);
        setImageBase64(null);
      })
      .finally(() => setIsLoadingImage(false));
  }, [effectiveTemplate?.imageId, effectiveTemplate?.image]);

  useEffect(() => {
    if (isLoadingTemplate || loadingCopyTemplate || !effectiveTemplate?.groups) {
      if (!effectiveTemplate) {
        lastSyncedTemplateKeyRef.current = null;
      }
      return;
    }

    const templateKey = String(effectiveTemplate?.id ?? effectiveTemplate?._sourceId ?? "new");
    if (lastSyncedTemplateKeyRef.current === templateKey) {
      return;
    }
    lastSyncedTemplateKeyRef.current = templateKey;

    const mappedGroups = mapGroups(effectiveTemplate.groups || []);
    const mappedEndpoints = mapEndpoints(effectiveTemplate.endpoints || [], mappedGroups);
    if (mappedGroups.length === 0) {
      const { group, endpoint } = createDefaultGroupAndEndpoint();
      setGroups([group]);
      setEndpoints([endpoint]);
      setSelectedGroupId(group.id);
    } else {
      setGroups(mappedGroups);
      if (mappedEndpoints.length === 0) {
        const firstGroup = mappedGroups[0];
        const { endpoint } = createDefaultGroupAndEndpoint();
        const defaultEp: Endpoints = {
          ...endpoint,
          groupId: firstGroup.id,
          id: `${firstGroup.id}-1`,
        };
        setEndpoints([defaultEp]);
      } else {
        setEndpoints(mappedEndpoints);
      }
      const active = mappedGroups.filter(
        (g: Groups) =>
          g.status === "Active" || (g.status === undefined && g.isEnabled !== false)
      );
      setSelectedGroupId(active.length > 0 ? active[0].id : null);
    }
    const mappedGreasePumps = mapGreasePumps(effectiveTemplate.greasePumps || []);
    const hasGreasePumpsEnabled =
      effectiveTemplate.isGreasePumpConnectivityEnabled || effectiveTemplate.hasGreasePumps || false;
    setGreasePumps(
      mappedGreasePumps.length > 0
        ? mappedGreasePumps
        : hasGreasePumpsEnabled
          ? [createDefaultGreasePump()]
          : []
    );
    const mappedGateways = (effectiveTemplate.gateways || []).map((g: Gateways) => ({
      ...g,
      status: g.status ?? "Active",
    }));
    setGateways(mappedGateways.length > 0 ? mappedGateways : [{ ...defaultGateway }]);
    setHasGreasePumps(
      effectiveTemplate.isGreasePumpConnectivityEnabled || effectiveTemplate.hasGreasePumps || false
    );
    if (effectiveTemplate.image) setImageBase64(effectiveTemplate.image);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingTemplate,
    loadingCopyTemplate,
    effectiveTemplate?.groups,
    effectiveTemplate?.endpoints,
    effectiveTemplate?.gateways,
    effectiveTemplate?.greasePumps,
    effectiveTemplate?.isGreasePumpConnectivityEnabled,
    effectiveTemplate?.hasGreasePumps,
    effectiveTemplate?.image,
  ]);

  const activeGroups = useMemo(
    () =>
      groups.filter(
        (g) => g.status === "Active" || (g.status === undefined && g.isEnabled !== false)
      ),
    [groups]
  );
  const activeGateways = useMemo(
    () => gateways.filter((g) => g.status === "Active" || g.status === undefined),
    [gateways]
  );

  useEffect(() => {
    if (activeGroups.length > 0 && selectedGroupId === null) {
      setSelectedGroupId(activeGroups[0].id);
    }
  }, [activeGroups, selectedGroupId]);

  const canProceedFromConfiguration = useMemo(() => {
    if (!imageBase64 || typeof imageBase64 !== "string" || imageBase64.length === 0) return false;
    if (activeGroups.length === 0) return false;
    if (activeGateways.length === 0) return false;
    const activeEndpoints = endpoints.filter(
      (ep) => ep.status === "Active" || (ep.status === undefined && ep.isEnabled !== false)
    );
    const hasGroupWithEndpoint = activeGroups.every((group) =>
      activeEndpoints.some((ep) => ep.groupId === group.id)
    );
    if (!hasGroupWithEndpoint) return false;
    const endpointNames = activeEndpoints.map((ep) => (ep.name || "").trim().toLowerCase()).filter(Boolean);
    if (endpointNames.length !== new Set(endpointNames).size) return false;
    const groupNames = activeGroups.map((g) => (g.name || "").trim().toLowerCase()).filter(Boolean);
    if (groupNames.length !== new Set(groupNames).size) return false;
    const gatewayNames = activeGateways.map((g) => (g.name || "").trim().toLowerCase()).filter(Boolean);
    if (gatewayNames.length !== new Set(gatewayNames).size) return false;
    return true;
  }, [imageBase64, activeGroups, activeGateways, endpoints]);

  const canSaveFromGreasePump = useMemo(() => {
    if (!hasGreasePumps) return true;
    const activeGreasePumps = greasePumps.filter(
      (p) => p.status === "Active" || (p.status === undefined && p.isEnabled !== false)
    );
    if (activeGreasePumps.length === 0) return false;
    const names = activeGreasePumps.map((p) => (p.name || "").trim().toLowerCase()).filter(Boolean);
    return names.length === new Set(names).size;
  }, [hasGreasePumps, greasePumps]);

  const getInitialValues = () =>
    getInitialValuesFromTemplate(effectiveTemplate, {
      showTemplateSourceChoice,
      isAdministrationMode,
      currentSiteId,
      currentSiteName,
    });

  const handleGreasePumpChange = (value: boolean) => {
    setHasGreasePumps(value);
    if (!value && step === 2) setStep(1);
  };

  const handlePrevious = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setStep((s) => s - 1);
  };

  const handleSelectNew = () => {
    setSourceOption("new");
    setBaseTemplate(null);
    setSelectedExistingTemplateId(null);
  };

  return {
    // Source choice (new vs copy)
    sourceOption,
    setSourceOption,
    baseTemplate,
    selectedExistingTemplateId,
    loadingCopyTemplate,
    handleExistingTemplateSelect,
    handleSelectNew,
    effectiveTemplate,
    showSourceChoiceInDetails,
    // Step & form state
    step,
    setStep,
    hasGreasePumps,
    setHasGreasePumps,
    groups,
    setGroups,
    endpoints,
    setEndpoints,
    gateways,
    setGateways,
    greasePumps,
    setGreasePumps,
    selectedGroupId,
    setSelectedGroupId,
    imageBase64,
    setImageBase64,
    isLoadingImage,
    isEdit,
    // Derived
    activeGroups,
    activeGateways,
    canProceedFromConfiguration,
    canSaveFromGreasePump,
    getInitialValues,
    handleGreasePumpChange,
    handlePrevious,
  };
}
